/**
 * Authentication hook. Wraps the tRPC `auth.me` query and `auth.logout`
 * mutation. Login itself happens via the /login page posting to /api/auth/login.
 *
 * When `redirectOnUnauthenticated` is set, this hook also enforces the
 * forced-password-change flow: any signed-in user with mustChangePassword=true
 * is bounced to /change-password before they can use the rest of the app.
 */
import { trpc } from "@/lib/trpc";
import { TRPCClientError } from "@trpc/client";
import { useCallback, useEffect, useMemo } from "react";

const LOGIN_PATH = "/login";
const CHANGE_PASSWORD_PATH = "/change-password";

type UseAuthOptions = {
  redirectOnUnauthenticated?: boolean;
};

export function useAuth(options?: UseAuthOptions) {
  const { redirectOnUnauthenticated = false } = options ?? {};
  const utils = trpc.useUtils();

  const meQuery = trpc.auth.me.useQuery(undefined, {
    retry: false,
    refetchOnWindowFocus: false,
  });

  const logoutMutation = trpc.auth.logout.useMutation({
    onSuccess: () => {
      utils.auth.me.setData(undefined, null);
    },
  });

  const logout = useCallback(async () => {
    try {
      await logoutMutation.mutateAsync();
    } catch (error: unknown) {
      if (error instanceof TRPCClientError && error.data?.code === "UNAUTHORIZED") {
        // Already logged out — fall through to redirect.
      } else {
        console.error("Logout error:", error);
      }
    } finally {
      utils.auth.me.setData(undefined, null);
      window.location.href = LOGIN_PATH;
    }
  }, [logoutMutation, utils]);

  const state = useMemo(
    () => ({
      user: meQuery.data ?? null,
      loading: meQuery.isLoading || logoutMutation.isPending,
      error: meQuery.error ?? logoutMutation.error ?? null,
      isAuthenticated: Boolean(meQuery.data),
    }),
    [meQuery.data, meQuery.error, meQuery.isLoading, logoutMutation.error, logoutMutation.isPending],
  );

  useEffect(() => {
    if (!redirectOnUnauthenticated) return;
    if (meQuery.isLoading || logoutMutation.isPending) return;
    if (typeof window === "undefined") return;

    const path = window.location.pathname;

    // Unauthenticated → /login (unless already there)
    if (!state.user) {
      if (path !== LOGIN_PATH) window.location.href = LOGIN_PATH;
      return;
    }

    // Authenticated but must rotate temporary password → /change-password
    if (state.user.mustChangePassword && path !== CHANGE_PASSWORD_PATH) {
      window.location.href = CHANGE_PASSWORD_PATH;
    }
  }, [redirectOnUnauthenticated, logoutMutation.isPending, meQuery.isLoading, state.user]);

  return {
    ...state,
    refresh: () => meQuery.refetch(),
    logout,
  };
}
